import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Avatar,
  Dropdown,
  DatePicker,
  Badge
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LogoutOutlined,
  TeamOutlined,
  HomeOutlined,
  UsergroupAddOutlined,
  EyeOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function ClassroomManagement() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [enrollModalVisible, setEnrollModalVisible] = useState(false);
  const [viewStudentsModalVisible, setViewStudentsModalVisible] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [classroomStudents, setClassroomStudents] = useState([]);
  const [form] = Form.useForm();
  const [enrollForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [classroomsRes, trainingsRes, facilitatorsRes, studentsRes] = await Promise.allSettled([
        api.get('/classrooms'),
        api.get('/trainings'),
        api.get('/users?role=facilitator'),
        api.get('/users?role=student')
      ]);

      setClassrooms(classroomsRes.status === 'fulfilled' ? classroomsRes.value.data : []);
      setTrainings(trainingsRes.status === 'fulfilled' ? trainingsRes.value.data : []);
      setFacilitators(facilitatorsRes.status === 'fulfilled' ? facilitatorsRes.value.data : []);
      setStudents(studentsRes.status === 'fulfilled' ? studentsRes.value.data : []);
    } catch (error) {
      message.error('Failed to load data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const handleCreate = () => {
    setEditingClassroom(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingClassroom(record);
    form.setFieldsValue({
      name: record.name,
      training_id: record.training_id,
      facilitator_id: record.facilitator_id,
      description: record.description,
      dates: record.start_date && record.end_date ? [
        dayjs(record.start_date),
        dayjs(record.end_date)
      ] : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (classroomId) => {
    try {
      await api.delete(`/classrooms/${classroomId}`);
      message.success('Classroom deleted successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to delete classroom');
      console.error('Error deleting classroom:', error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Format dates if provided
      const payload = {
        ...values,
        start_date: values.dates?.[0]?.format('YYYY-MM-DD'),
        end_date: values.dates?.[1]?.format('YYYY-MM-DD'),
      };
      delete payload.dates;

      if (editingClassroom) {
        await api.put(`/classrooms/${editingClassroom.id}`, payload);
        message.success('Classroom updated successfully');
      } else {
        await api.post('/classrooms', payload);
        message.success('Classroom created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error(editingClassroom ? 'Failed to update classroom' : 'Failed to create classroom');
      console.error('Error saving classroom:', error);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingClassroom(null);
  };

  const handleEnrollStudents = (record) => {
    setSelectedClassroom(record);
    enrollForm.resetFields();
    setEnrollModalVisible(true);
  };

  const handleEnrollOk = async () => {
    try {
      const values = await enrollForm.validateFields();

      await api.post(`/classrooms/${selectedClassroom.id}/enroll`, {
        student_ids: values.student_ids
      });

      message.success('Students enrolled successfully');
      setEnrollModalVisible(false);
      enrollForm.resetFields();
      fetchData();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error('Failed to enroll students');
      console.error('Error enrolling students:', error);
    }
  };

  const handleEnrollCancel = () => {
    setEnrollModalVisible(false);
    enrollForm.resetFields();
    setSelectedClassroom(null);
  };

  const handleViewStudents = async (record) => {
    setSelectedClassroom(record);
    try {
      const response = await api.get(`/classrooms/${record.id}/students`);
      setClassroomStudents(response.data);
      setViewStudentsModalVisible(true);
    } catch (error) {
      message.error('Failed to load students');
      console.error('Error fetching classroom students:', error);
      setClassroomStudents([]);
      setViewStudentsModalVisible(true);
    }
  };

  const handleUnenroll = async (studentId) => {
    try {
      await api.delete(`/classrooms/${selectedClassroom.id}/students/${studentId}`);
      message.success('Student unenrolled successfully');
      handleViewStudents(selectedClassroom); // Refresh the list
      fetchData();
    } catch (error) {
      message.error('Failed to unenroll student');
      console.error('Error unenrolling student:', error);
    }
  };

  const columns = [
    {
      title: 'Classroom',
      key: 'classroom',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#1890ff' }} icon={<TeamOutlined />} />
          <div>
            <div><Text strong>{record.name}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>
              {record.description?.substring(0, 50)}...
            </Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Training',
      dataIndex: 'training_id',
      key: 'training',
      render: (trainingId) => {
        const training = trainings.find(t => t.id === trainingId);
        return training ? (
          <Space>
            <BookOutlined style={{ color: '#faad14' }} />
            <Text>{training.name}</Text>
          </Space>
        ) : <Text type="secondary">N/A</Text>;
      },
    },
    {
      title: 'Facilitator',
      dataIndex: 'facilitator_id',
      key: 'facilitator',
      render: (facilitatorId) => {
        const facilitator = facilitators.find(f => f.id === facilitatorId);
        return facilitator ? (
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <Text>{facilitator.first_name} {facilitator.last_name}</Text>
          </Space>
        ) : <Text type="secondary">N/A</Text>;
      },
    },
    {
      title: 'Students',
      dataIndex: 'student_count',
      key: 'student_count',
      render: (count) => (
        <Badge count={count || 0} style={{ backgroundColor: '#52c41a' }} />
      ),
      sorter: (a, b) => (a.student_count || 0) - (b.student_count || 0),
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => {
        if (record.start_date && record.end_date) {
          return (
            <Space direction="vertical" size="small">
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(record.start_date).format('MMM DD, YYYY')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                to {dayjs(record.end_date).format('MMM DD, YYYY')}
              </Text>
            </Space>
          );
        }
        return <Text type="secondary">Not scheduled</Text>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const now = dayjs();
        const start = record.start_date ? dayjs(record.start_date) : null;
        const end = record.end_date ? dayjs(record.end_date) : null;

        if (!start || !end) {
          return <Tag>Not Scheduled</Tag>;
        }
        if (now.isBefore(start)) {
          return <Tag color="blue">Upcoming</Tag>;
        }
        if (now.isAfter(end)) {
          return <Tag color="default">Completed</Tag>;
        }
        return <Tag color="green">Active</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewStudents(record)}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<UsergroupAddOutlined />}
            onClick={() => handleEnrollStudents(record)}
          >
            Enroll
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete classroom"
            description="Are you sure you want to delete this classroom?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const studentColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <Text>{record.first_name} {record.last_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Unenroll student"
          description="Are you sure you want to unenroll this student?"
          onConfirm={() => handleUnenroll(record.id)}
          okText="Yes"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button type="link" danger size="small">
            Unenroll
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }}
            onClick={() => navigate('/admin/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Classroom Management</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>Classroom Management</Title>
            <Text type="secondary">Create and manage classrooms, enroll students</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleCreate}
          >
            Create Classroom
          </Button>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={classrooms}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} classrooms`,
            }}
          />
        </Card>
      </Content>

      {/* Create/Edit Classroom Modal */}
      <Modal
        title={editingClassroom ? 'Edit Classroom' : 'Create New Classroom'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText={editingClassroom ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Classroom Name"
            rules={[{ required: true, message: 'Please enter classroom name' }]}
          >
            <Input placeholder="e.g., Cybersecurity Spring 2024" />
          </Form.Item>

          <Form.Item
            name="training_id"
            label="Training Program"
            rules={[{ required: true, message: 'Please select a training' }]}
          >
            <Select placeholder="Select training program">
              {trainings.map(training => (
                <Select.Option key={training.id} value={training.id}>
                  {training.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="facilitator_id"
            label="Facilitator"
            rules={[{ required: true, message: 'Please select a facilitator' }]}
          >
            <Select placeholder="Select facilitator">
              {facilitators.map(facilitator => (
                <Select.Option key={facilitator.id} value={facilitator.id}>
                  {facilitator.first_name} {facilitator.last_name} ({facilitator.email})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dates"
            label="Duration (Optional)"
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={4}
              placeholder="Describe the classroom objectives and any special instructions..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Enroll Students Modal */}
      <Modal
        title={`Enroll Students to: ${selectedClassroom?.name}`}
        open={enrollModalVisible}
        onOk={handleEnrollOk}
        onCancel={handleEnrollCancel}
        width={700}
        okText="Enroll Students"
      >
        <Form
          form={enrollForm}
          layout="vertical"
        >
          <Form.Item
            name="student_ids"
            label="Select Students"
            rules={[{ required: true, message: 'Please select at least one student' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select students to enroll"
              style={{ width: '100%' }}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={students.map(student => ({
                value: student.id,
                label: `${student.first_name} ${student.last_name} (${student.email})`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Students Modal */}
      <Modal
        title={`Students in: ${selectedClassroom?.name}`}
        open={viewStudentsModalVisible}
        onCancel={() => {
          setViewStudentsModalVisible(false);
          setClassroomStudents([]);
          setSelectedClassroom(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewStudentsModalVisible(false);
            setClassroomStudents([]);
            setSelectedClassroom(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
      >
        <Table
          columns={studentColumns}
          dataSource={classroomStudents}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showTotal: (total) => `Total ${total} students enrolled`,
          }}
        />
      </Modal>
    </Layout>
  );
}